import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Server,
  Search,
  Plus,
  Copy,
  MoreHorizontal,
  RefreshCw,
  Play,
  Square,
  RotateCw,
  PauseCircle,
  Trash2,
  Filter,
  SlidersHorizontal,
  Download,
} from 'lucide-react';
import { Toolbar, PageHeader, Container } from '@vnetwork/ui/layout';
import styles from './InstanceList.module.css';
import { DataTable, StatusPill, EmptyState, FilterBuilder, type ColumnDef, type FilterField, type FilterCondition } from '@vnetwork/ui/data';
import {
  Button,
  Input,
  Skeleton,
  IconButton,
  Popover,
  PopoverTrigger,
  PopoverContent,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuCheckboxItem,
} from '@vnetwork/ui/primitives';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { HaltConfirmDialog } from './InstanceActionBar';
import {
  useInstanceList,
  useInstanceSummaries,
  useInstanceAction,
  useOpennebula,
  type Instance,
  type InstanceAction,
  type InstanceSummary,
} from '@/api/opennebula';
import { instanceStateKey, instanceTone, instanceLabel, instanceBucket, copyToClipboard } from '../shared';

// Columns the user can toggle via "Display options". Instance ID + Name are
// fixed at the front; actions column is fixed at the end.
const TOGGLEABLE = [
  { id: 'state',    label: 'State' },
  { id: 'ip',       label: 'Assigned IP' },
  { id: 'floating', label: 'Floating IP' },
  { id: 'flavor',   label: 'Type' },
  { id: 'image',    label: 'Image' },
  { id: 'zone',     label: 'Zone' },
] as const;

const DEFAULT_VISIBLE: Record<string, boolean> = {
  state: true, ip: true, floating: true, flavor: true, image: true, zone: true,
};

/** Download the current filtered rows as a CSV file (current page only). */
function exportCsv(rows: Instance[], filename: string) {
  const head = ['id', 'name', 'state', 'ip', 'type', 'image', 'zone'];
  const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const body = rows.map((r) => {
    const publicIp = r.metadata?.nics?.find((n) => /public/i.test(n.network ?? ''))?.ip
      ?? r.metadata?.nics?.[0]?.ip
      ?? '';
    return [
      esc(r.id),
      esc(r.name),
      esc(r.state ?? r.status ?? ''),
      esc(publicIp),
      esc(r.metadata?.flavor ?? ''),
      esc(r.metadata?.image?.name ?? ''),
      esc(r.zone ?? ''),
    ].join(',');
  }).join('\n');
  const blob = new Blob([`${head.join(',')}\n${body}`], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// Valid instance actions by power state, mirroring InstanceActionBar logic.
// Only valid actions appear in the row dropdown to avoid dispatching no-ops.
const ACTIONS_WHEN_ON: InstanceAction[] = ['poweroff', 'reboot', 'suspend'];
const ACTIONS_WHEN_OFF: InstanceAction[] = ['resume'];

const ACTION_LABELS: Record<InstanceAction, string> = {
  resume: 'Start',
  poweroff: 'Power off',
  reboot: 'Reboot',
  suspend: 'Suspend',
  terminate: 'Terminate',
  poweron: 'Start',
  resize: 'Resize',
};

const ACTION_ICONS: Record<string, React.ReactNode> = {
  resume: <Play size={14} />,
  poweroff: <Square size={14} />,
  reboot: <RotateCw size={14} />,
  suspend: <PauseCircle size={14} />,
};

export function InstanceList() {
  const navigate = useNavigate();
  const { serviceId } = useParams<{ serviceId: string }>();
  const { data: instances = [], isLoading, isError, refetch } = useInstanceList(serviceId);
  const [search, setSearch] = useState('');
  const [conditions, setConditions] = useState<FilterCondition[]>([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [visible, setVisible] = useState<Record<string, boolean>>(DEFAULT_VISIBLE);
  const base = `/services/compute/${serviceId}`;

  // Batch fetch all instance summaries in one useQueries call (no per-row N+1).
  const instanceIds = useMemo(() => instances.map((i) => i.id), [instances]);
  const summaries = useInstanceSummaries(serviceId, instanceIds);

  // Derive filter option sets from the full list (not the filtered rows).
  // Options are {value,label} objects as required by FilterField.
  const stateOptions = useMemo(() => {
    const seen = new Set<string>();
    instances.forEach((i) => {
      const s = summaries[i.id];
      const key = instanceStateKey(s?.state ?? i.state, s?.status ?? i.status);
      if (key) seen.add(key);
    });
    return Array.from(seen).sort().map((v) => ({ value: v, label: instanceLabel(v) }));
  }, [instances, summaries]);

  const zoneOptions = useMemo(() => {
    const seen = new Set<string>();
    instances.forEach((i) => { if (i.zone) seen.add(i.zone); });
    return Array.from(seen).sort().map((v) => ({ value: v, label: v }));
  }, [instances]);

  const imageOptions = useMemo(() => {
    const seen = new Set<string>();
    instances.forEach((i) => { if (i.metadata?.image?.name) seen.add(i.metadata.image.name); });
    return Array.from(seen).sort().map((v) => ({ value: v, label: v }));
  }, [instances]);

  const filterFields: FilterField[] = [
    { value: 'state', label: 'State', input: 'multiselect', operators: [{ value: 'is_any_of', label: 'is any of' }], options: stateOptions },
    { value: 'zone',  label: 'Zone',  input: 'multiselect', operators: [{ value: 'is_any_of', label: 'is any of' }], options: zoneOptions },
    { value: 'image', label: 'Image', input: 'multiselect', operators: [{ value: 'is_any_of', label: 'is any of' }], options: imageOptions },
  ];

  // Derive selected sets from FilterBuilder conditions (mirrors DomainList collect pattern).
  const collect = (field: string) =>
    conditions
      .filter((c) => c.field === field)
      .flatMap((c) => (c.value || '').split(','))
      .map((s) => s.trim())
      .filter(Boolean);
  const stateSel = collect('state');
  const zoneSel  = collect('zone');
  const imageSel = collect('image');

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return instances.filter((i) => {
      if (q && !i.name?.toLowerCase().includes(q)) return false;
      if (stateSel.length) {
        const s = summaries[i.id];
        const key = instanceStateKey(s?.state ?? i.state, s?.status ?? i.status);
        if (!key || !stateSel.includes(key)) return false;
      }
      if (zoneSel.length && (!i.zone || !zoneSel.includes(i.zone))) return false;
      if (imageSel.length) {
        const img = i.metadata?.image?.name;
        if (!img || !imageSel.includes(img)) return false;
      }
      return true;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instances, summaries, search, conditions]);

  const health = useMemo(() => {
    const buckets = { running: 0, stopped: 0, error: 0, other: 0 };
    for (const i of instances) {
      const s = summaries[i.id];
      buckets[instanceBucket(instanceStateKey(s?.state ?? i.state, s?.status ?? i.status))]++;
    }
    return buckets;
  }, [instances, summaries]);

  // All possible toggleable column definitions keyed by id.
  const toggleableCols = useMemo<Record<string, ColumnDef<Instance, unknown>>>(
    () => ({
      state: {
        id: 'state',
        header: 'State',
        enableSorting: false,
        cell: ({ row }) => {
          const s = summaries[row.original.id];
          const key = instanceStateKey(s?.state ?? row.original.state, s?.status ?? row.original.status);
          return <StatusPill tone={instanceTone(key)}>{instanceLabel(key)}</StatusPill>;
        },
      },
      ip: {
        id: 'ip',
        header: 'Assigned IP',
        enableSorting: false,
        cell: ({ row }) => <IpCell summary={summaries[row.original.id]} field="ip" />,
      },
      floating: {
        id: 'floating',
        header: 'Floating IP',
        enableSorting: false,
        cell: ({ row }) => <IpCell summary={summaries[row.original.id]} field="floating" />,
      },
      flavor: {
        id: 'flavor',
        header: 'Type',
        enableSorting: false,
        cell: ({ row }) => {
          const m = row.original.metadata;
          return (
            <span style={{ color: 'var(--vn-fg-muted)' }}>
              {m?.flavor ? `${m.flavor}${m.type ? ` (${m.type})` : ''}` : '—'}
            </span>
          );
        },
      },
      image: {
        id: 'image',
        header: 'Image',
        enableSorting: false,
        cell: ({ row }) => {
          const img = row.original.metadata?.image;
          return (
            <span style={{ color: 'var(--vn-fg-muted)' }}>
              {img?.name ? `${img.name}${img.distro ? ` (${img.distro})` : ''}` : '—'}
            </span>
          );
        },
      },
      zone: {
        id: 'zone',
        accessorKey: 'zone',
        header: 'Zone',
        enableSorting: false,
        cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)' }}>{row.original.zone || '—'}</span>,
      },
    }),
    [summaries],
  );

  const columns = useMemo<ColumnDef<Instance, unknown>[]>(
    () => [
      // Fixed: Instance ID + Name always visible
      {
        id: 'id',
        accessorKey: 'id',
        header: 'Instance ID',
        cell: ({ row }) => <span className={styles.idCell}>{row.original.id}</span>,
      },
      {
        id: 'name',
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => row.original.name || '—',
      },
      // Toggleable middle columns — rendered in TOGGLEABLE order, filtered by visibility state.
      ...TOGGLEABLE.filter((c) => visible[c.id]).map((c) => toggleableCols[c.id]),
      // Fixed: actions always visible at the end
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <ActionsCell
            serviceId={serviceId!}
            instance={row.original}
            summary={summaries[row.original.id]}
            base={base}
          />
        ),
      },
    ],
    [serviceId, base, summaries, visible, toggleableCols],
  );

  return (
    <Container size="xl">
      <PageHeader
        icon={<Server size={22} />}
        title={`Instances${instances.length ? ` (${instances.length})` : ''}`}
        description="Virtual machines allocated to this service."
        actions={
          <>
            <Button variant="secondary" startIcon={<RefreshCw size={15} />} onClick={() => refetch()}>
              Refresh
            </Button>
            <Button startIcon={<Plus size={15} />} onClick={() => navigate(`${base}/instances/create`)}>
              Create instance
            </Button>
          </>
        }
      />

      {instances.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: 'var(--vn-space-3)',
            marginBottom: 'var(--vn-space-4)',
          }}
        >
          <HealthChip label="Running" value={health.running} tone="success" />
          <HealthChip label="Powered off" value={health.stopped} tone="muted" />
          <HealthChip label="Error" value={health.error} tone="danger" />
          <HealthChip label="Other" value={health.other} tone="warning" />
        </div>
      )}

      {/* Toolbar with wrappable end cluster — three action buttons at phone
          widths would clip with the default non-wrapping Toolbar.end. */}
      <Toolbar
        className={styles.toolbar}
        start={
          <Input
            placeholder="Find instances by name"
            startSlot={<Search />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={styles.searchInput}
          />
        }
      >
        <div className={styles.toolbarEnd}>
          <Popover open={filterOpen} onOpenChange={setFilterOpen}>
            <PopoverTrigger asChild>
              <Button variant="secondary" startIcon={<Filter size={15} />}>
                Filters{conditions.length ? ` (${conditions.length})` : ''}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" style={{ padding: 0 }}>
              <FilterBuilder
                fields={filterFields}
                defaultValue={conditions.length ? conditions : undefined}
                onApply={(c) => {
                  setConditions(c.filter((x) => x.value.trim()));
                  setFilterOpen(false);
                }}
                onClose={() => setFilterOpen(false)}
              />
            </PopoverContent>
          </Popover>
          {conditions.length > 0 && (
            <Button variant="ghost" onClick={() => setConditions([])}>
              Clear
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" startIcon={<SlidersHorizontal size={15} />}>
                Display options
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Edit columns</DropdownMenuLabel>
              {TOGGLEABLE.map((c) => (
                <DropdownMenuCheckboxItem
                  key={c.id}
                  checked={visible[c.id]}
                  onCheckedChange={(v) => setVisible((prev) => ({ ...prev, [c.id]: v === true }))}
                  onSelect={(e) => e.preventDefault()}
                >
                  {c.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            variant="secondary"
            startIcon={<Download size={15} />}
            disabled={rows.length === 0}
            onClick={() => exportCsv(rows, 'compute-instances.csv')}
          >
            Export page
          </Button>
        </div>
      </Toolbar>

      {isError ? (
        <EmptyState
          icon={<Server size={28} />}
          title="Couldn't load instances"
          description="Something went wrong fetching this service's virtual machines."
          actions={
            <Button variant="secondary" startIcon={<RefreshCw size={15} />} onClick={() => refetch()}>
              Retry
            </Button>
          }
        />
      ) : isLoading ? (
        <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} style={{ height: 48 }} />
          ))}
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          getRowId={(r) => String(r.id)}
          onRowClick={(r) => navigate(`${base}/instances/${r.id}`)}
          footer={`Showing ${rows.length} of ${instances.length}`}
          emptyState={
            instances.length === 0 ? (
              <EmptyState
                icon={<Server size={28} />}
                title="No instances yet"
                description="Launch your first virtual machine to get started."
                actions={
                  <Button startIcon={<Plus size={15} />} onClick={() => navigate(`${base}/instances/create`)}>
                    Create instance
                  </Button>
                }
              />
            ) : (
              "No instances match your search or filters."
            )
          }
        />
      )}
    </Container>
  );
}

// ---- Health-status count band --------------------------------------------

function HealthChip({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'success' | 'muted' | 'danger' | 'warning';
}) {
  const color =
    tone === 'success'
      ? 'var(--vn-success-solid)'
      : tone === 'danger'
        ? 'var(--vn-danger-solid)'
        : tone === 'warning'
          ? 'var(--vn-warning-solid)'
          : 'var(--vn-fg-muted)';
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'var(--vn-space-2)',
        padding: 'var(--vn-space-2) var(--vn-space-3)',
        border: '1px solid var(--vn-border-default)',
        borderRadius: 'var(--vn-radius-md)',
        background: 'var(--vn-bg-surface)',
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)', color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
        <span style={{ width: 8, height: 8, borderRadius: 'var(--vn-radius-full)', background: color }} />
        {label}
      </span>
      <span style={{ fontWeight: 700, fontSize: 'var(--vn-font-size-lg)' }}>{value}</span>
    </div>
  );
}

// ---- Per-row IP cell (reads from shared summary map, no individual fetch) ----

function IpCell({ summary, field }: { summary: InstanceSummary | undefined; field: 'ip' | 'floating' }) {
  const { show } = useToast();
  const value = (summary?.[field] as string | undefined) || '';
  if (!value || value === '-') return <span style={{ color: 'var(--vn-fg-subtle)' }}>—</span>;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-1)' }}>
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>
      <IconButton
        variant="ghost"
        size="sm"
        aria-label={`Copy ${value}`}
        onClick={async (e) => {
          e.stopPropagation();
          if (await copyToClipboard(value)) show({ tone: 'success', title: 'IP copied to clipboard' });
        }}
      >
        <Copy size={13} />
      </IconButton>
    </span>
  );
}

// ---- Row action dropdown (state-aware, mirrors InstanceActionBar logic) -----

function ActionsCell({
  serviceId,
  instance,
  summary,
  base,
}: {
  serviceId: string;
  instance: Instance;
  summary: InstanceSummary | undefined;
  base: string;
}) {
  const navigate = useNavigate();
  const { show } = useToast();
  const { setWaiting } = useOpennebula();
  const [confirmTerminate, setConfirmTerminate] = useState(false);
  const [confirmReboot, setConfirmReboot] = useState(false);
  // Halt (poweroff/suspend) uses the same dialog as the detail action bar so users
  // see the cost warning + resource breakdown before stopping.
  const [confirmHalt, setConfirmHalt] = useState<'poweroff' | 'suspend' | null>(null);

  const action = useInstanceAction(serviceId, instance.id, {
    onSuccess: (_d, perform) => {
      setWaiting(perform);
      show({ tone: 'success', title: `${ACTION_LABELS[perform]} requested` });
    },
    onError: () => show({ tone: 'danger', title: 'Action failed' }),
  });

  const run = (perform: InstanceAction) => action.mutate(perform);

  // Derive live power state from the batch summary (falls back to list data).
  const status = summary?.status ?? instance.status ?? '';
  const isOn = status === 'POWERED_ON';
  // Terminated instances have no valid lifecycle transitions — the server rejects
  // them. Match the action bar's guard: only show Manage for terminated rows.
  const isTerminated = status === 'TERMINATED';
  const validActions = isTerminated ? [] : isOn ? ACTIONS_WHEN_ON : ACTIONS_WHEN_OFF;

  return (
    <div style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <IconButton variant="ghost" size="sm" aria-label="Instance actions" disabled={action.isPending}>
            <MoreHorizontal size={16} />
          </IconButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => navigate(`${base}/instances/${instance.id}`)}>
            Manage
          </DropdownMenuItem>
          {validActions.map((act) => {
            // Destructive or disruptive actions gate through a confirm dialog.
            if (act === 'reboot') {
              return (
                <DropdownMenuItem key={act} onSelect={() => setConfirmReboot(true)}>
                  {ACTION_ICONS[act]} {ACTION_LABELS[act]}
                </DropdownMenuItem>
              );
            }
            if (act === 'poweroff' || act === 'suspend') {
              return (
                <DropdownMenuItem key={act} onSelect={() => setConfirmHalt(act)}>
                  {ACTION_ICONS[act]} {ACTION_LABELS[act]}
                </DropdownMenuItem>
              );
            }
            return (
              <DropdownMenuItem key={act} onSelect={() => run(act)}>
                {ACTION_ICONS[act]} {ACTION_LABELS[act]}
              </DropdownMenuItem>
            );
          })}
          {!isTerminated && (
            <DropdownMenuItem tone="danger" onSelect={() => setConfirmTerminate(true)}>
              <Trash2 size={14} /> Terminate
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <ConfirmDialog
        open={confirmReboot}
        onOpenChange={setConfirmReboot}
        title="Reboot instance?"
        description={`"${instance.name || instance.id}" will restart. Any unsaved work may be lost.`}
        confirmLabel="Reboot"
        loading={action.isPending}
        onConfirm={() => {
          run('reboot');
          setConfirmReboot(false);
        }}
      />

      <ConfirmDialog
        open={confirmTerminate}
        onOpenChange={setConfirmTerminate}
        title="Terminate instance?"
        description={`This permanently destroys "${instance.name || instance.id}" and its disks. This cannot be undone.`}
        confirmLabel="Terminate"
        destructive
        requireText={instance.name || instance.id}
        loading={action.isPending}
        onConfirm={() => {
          run('terminate');
          setConfirmTerminate(false);
        }}
      />

      <HaltConfirmDialog
        action={confirmHalt}
        instance={instance}
        loading={action.isPending}
        onOpenChange={(o) => !o && setConfirmHalt(null)}
        onConfirm={() => {
          if (confirmHalt) run(confirmHalt);
          setConfirmHalt(null);
        }}
      />
    </div>
  );
}
